import { useCallback, useEffect, useLayoutEffect, useRef, type RefObject } from "react";

/**
 * Where the Chats list holds the reader (slice 1 spec §7.1). It reads like a
 * channel, newest at the bottom: it opens there and stays pinned while the
 * reader is there. Scrolled up, the rows in view hold still while the list
 * changes around them: an older page loading above, a thread moving to the
 * bottom, the list shown again after a phone's open thread.
 */

/** A scroller's measurements, as the DOM has them. */
export type ScrollBox = { scrollTop: number; scrollHeight: number; clientHeight: number };

/** A row: its key, a stamp that changes when it moves, and where it sits in the scrolled content. */
export type RowBox = { key: string; stamp: string; top: number; height: number };

type Anchor = { key: string; stamp: string; offset: number };

export type Hold = { pinned: true } | { pinned: false; anchors: Anchor[] };

/** Within this of the bottom is at the bottom. */
const SLACK = 8;

export function atBottom(box: ScrollBox): boolean {
  return box.scrollHeight - box.scrollTop - box.clientHeight <= SLACK;
}

/** Where the reader is: at the bottom, or the rows in view, each with how far it sits below the top. */
export function holdOf(box: ScrollBox, rows: RowBox[]): Hold {
  if (atBottom(box)) return { pinned: true };
  const bottom = box.scrollTop + box.clientHeight;
  const anchors = rows
    .filter((r) => r.top + r.height > box.scrollTop && r.top < bottom)
    .map((r) => ({ key: r.key, stamp: r.stamp, offset: r.top - box.scrollTop }));
  return { pinned: false, anchors };
}

/**
 * Where the reader is after a scroll whose event hadn't come before the list
 * changed. Scrolled to where the bottom was (`seenHeight`, the content's
 * height before the change), they're at the bottom: a thread that arrived
 * meanwhile goes under them, not out of view.
 */
export function holdAfterScroll(box: ScrollBox, rows: RowBox[], seenHeight: number): Hold {
  if (box.scrollTop + box.clientHeight >= seenHeight - SLACK) return { pinned: true };
  return holdOf(box, rows);
}

/**
 * The `scrollTop` that keeps `hold` once the list has changed: the bottom, or
 * the first row that was in view and hasn't moved, where it was. A row that
 * moved (its stamp changed) went to the bottom, and following it would yank
 * the reader down. Null when nothing in view is left to hold on to.
 */
export function scrollTopFor(hold: Hold, box: ScrollBox, rows: RowBox[]): number | null {
  if (hold.pinned) return Math.max(0, box.scrollHeight - box.clientHeight);
  for (const a of hold.anchors) {
    const row = rows.find((r) => r.key === a.key && r.stamp === a.stamp);
    if (row) return row.top - a.offset;
  }
  return null;
}

/** The rows under `el` (`data-row-key`, `data-row-stamp`), measured in its scrolled content. */
function rowsIn(el: HTMLElement): RowBox[] {
  const origin = el.getBoundingClientRect().top - el.scrollTop;
  return Array.from(el.querySelectorAll<HTMLElement>("[data-row-key]"), (row) => {
    const rect = row.getBoundingClientRect();
    return {
      key: row.dataset.rowKey ?? "",
      stamp: row.dataset.rowStamp ?? "",
      top: rect.top - origin,
      height: rect.height,
    };
  });
}

/**
 * Holds a scroller where its reader is (above). Put `ref` and `onScroll` on
 * the scroller, with `overflow-anchor: none` so only this moves it; its rows
 * carry `data-row-key` and `data-row-stamp`. A hidden scroller (a phone's
 * list behind the open thread) is left alone until it shows again.
 */
export function useHeldAtBottom<E extends HTMLElement>(): {
  ref: RefObject<E | null>;
  onScroll: () => void;
} {
  const ref = useRef<E | null>(null);
  const hold = useRef<Hold>({ pinned: true });
  // Where the scroller was, and its content's height, when `hold` was taken;
  // null while it's hidden.
  const seen = useRef<{ top: number; height: number } | null>(null);
  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    if (el.clientHeight === 0) {
      seen.current = null;
      return;
    }
    hold.current = holdOf(el, rowsIn(el));
    seen.current = { top: el.scrollTop, height: el.scrollHeight };
  }, []);
  const settle = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    if (el.clientHeight === 0) {
      seen.current = null;
      return;
    }
    // The reader scrolled and its scroll event hasn't come yet: where they are now wins.
    if (seen.current !== null && Math.abs(el.scrollTop - seen.current.top) >= 1) {
      hold.current = holdAfterScroll(el, rowsIn(el), seen.current.height);
    }
    const top = scrollTopFor(hold.current, el, rowsIn(el));
    if (top !== null && Math.abs(el.scrollTop - top) >= 1) el.scrollTop = top;
    measure();
  }, [measure]);
  // After every render, before it paints: the rows may have changed.
  useLayoutEffect(settle);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const resized = new ResizeObserver(settle);
    resized.observe(el);
    if (el.firstElementChild) resized.observe(el.firstElementChild);
    return () => resized.disconnect();
  }, [settle]);
  return { ref, onScroll: measure };
}
